import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Typography, Box, Grid, Paper, Stack, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, MenuItem, CircularProgress, Alert, TableSortLabel,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  CalendarMonth as CalendarIcon,
  History as HistoryIcon,
  ReceiptLong as ReceiptIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as PaidIcon,
  Warning as OverdueIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import InvoiceService from '../../services/InvoiceService';
import VisibleDateField from '../../components/invoice/VisibleDateField';

const invoiceService = new InvoiceService();

const statusColors = {
  draft: 'default',
  pending: 'warning',
  paid: 'success',
  overdue: 'error',
  voided: 'default',
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);

const formatDateInput = (date) => date.toISOString().slice(0, 10);

const getFinancialYearRange = (referenceDate = new Date(), yearOffset = 0) => {
  const month = referenceDate.getMonth();
  const year = referenceDate.getFullYear();
  const fyStartYear = (month >= 6 ? year : year - 1) + yearOffset;

  return {
    start: new Date(fyStartYear, 6, 1),
    end: new Date(fyStartYear + 1, 5, 30, 23, 59, 59, 999),
    label: `FY ${fyStartYear}/${String(fyStartYear + 1).slice(-2)}`,
  };
};

const getRangeForTimeframe = (timeframe, customFrom, customTo) => {
  if (timeframe === 'previous-fy') {
    return getFinancialYearRange(new Date(), -1);
  }

  if (timeframe === 'absolute') {
    return {
      start: customFrom ? new Date(`${customFrom}T00:00:00`) : null,
      end: customTo ? new Date(`${customTo}T23:59:59.999`) : null,
      label: customFrom && customTo ? `${customFrom} to ${customTo}` : 'Custom range',
    };
  }

  return getFinancialYearRange(new Date(), 0);
};

const buildInvoiceBasWorkbook = (invoices, periodLabel) => {
  const summaryRows = [
    ['Labourando BAS Invoice Export'],
    ['Period', periodLabel],
    ['Generated', new Date().toISOString()],
    [],
    ['Metric', 'Amount'],
    ['Invoice count', invoices.length],
    ['Taxable sales (ex GST)', invoices.reduce((sum, invoice) => sum + (invoice.subtotal || 0), 0)],
    ['GST collected', invoices.reduce((sum, invoice) => sum + (invoice.gstAmount || 0), 0)],
    ['Total invoiced', invoices.reduce((sum, invoice) => sum + (invoice.total || 0), 0)],
  ];

  const detailRows = invoices.map((invoice) => ({
    'Issue Date': invoice.issueDate,
    'Invoice #': invoice.invoiceNumber,
    'Customer': invoice.organisationSnapshot?.name || '',
    'Status': invoice.status,
    'Subtotal (ex GST)': invoice.subtotal || 0,
    'GST': invoice.gstAmount || 0,
    'Total': invoice.total || 0,
    'Due Date': invoice.dueDate,
  }));

  const workbook = XLSX.utils.book_new();
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
  const detailSheet = XLSX.utils.json_to_sheet(detailRows);

  XLSX.utils.book_append_sheet(workbook, summarySheet, 'BAS Summary');
  XLSX.utils.book_append_sheet(workbook, detailSheet, 'Invoices');

  return workbook;
};

const InvoiceDashboard = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('this-fy');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const invoiceData = await invoiceService.getAll(statusFilter || undefined);
      setInvoices(invoiceData);
    } catch (err) {
      setError(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const timeframeRange = useMemo(
    () => getRangeForTimeframe(timeframe, customFrom, customTo),
    [timeframe, customFrom, customTo],
  );

  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      const invoiceDate = new Date(invoice.issueDate);
      const hasRange = Boolean(timeframeRange.start || timeframeRange.end);

      if (hasRange) {
        if (timeframeRange.start && invoiceDate < timeframeRange.start) return false;
        if (timeframeRange.end && invoiceDate > timeframeRange.end) return false;
      }

      return true;
    });
  }, [invoices, timeframeRange]);

  const dashboardInsights = useMemo(() => {
    const summary = {
      totalInvoices: filteredInvoices.length,
      draft: { count: 0, total: 0 },
      pending: { count: 0, total: 0 },
      paid: { count: 0, total: 0 },
      overdue: { count: 0, total: 0 },
      voided: { count: 0, total: 0 },
    };

    filteredInvoices.forEach((invoice) => {
      const status = invoice.status || 'draft';
      if (!summary[status]) return;
      summary[status].count += 1;
      summary[status].total += invoice.total || 0;
    });

    return summary;
  }, [filteredInvoices]);

  const insightCards = dashboardInsights
    ? [
        { label: 'Outstanding', value: formatCurrency(dashboardInsights.pending.total), count: dashboardInsights.pending.count, icon: PendingIcon, color: 'warning.main' },
        { label: 'Paid', value: formatCurrency(dashboardInsights.paid.total), count: dashboardInsights.paid.count, icon: PaidIcon, color: 'success.main' },
        { label: 'Overdue', value: formatCurrency(dashboardInsights.overdue.total), count: dashboardInsights.overdue.count, icon: OverdueIcon, color: 'error.main' },
        { label: 'Total Invoices', value: dashboardInsights.totalInvoices, count: null, icon: TrendingUpIcon, color: 'info.main' },
      ]
    : [];

  const handleSort = (field) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };
  const visibleInvoices = useMemo(() => {
    return [...filteredInvoices].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'invoiceNumber':
          comparison = String(a.invoiceNumber || '').localeCompare(String(b.invoiceNumber || ''));
          break;
        case 'issueDate':
          comparison = new Date(a.issueDate || 0).getTime() - new Date(b.issueDate || 0).getTime();
          break;
        case 'dueDate':
          comparison = new Date(a.dueDate || 0).getTime() - new Date(b.dueDate || 0).getTime();
          break;
        case 'organisation':
          comparison = String(a.organisationSnapshot?.name || '').localeCompare(String(b.organisationSnapshot?.name || ''));
          break;
        case 'total':
          comparison = (a.total || 0) - (b.total || 0);
          break;
        case 'status':
          comparison = String(a.status || '').localeCompare(String(b.status || ''));
          break;
        case 'createdAt':
        default:
          comparison = new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredInvoices, sortField, sortDirection]);

  const paginatedInvoices = visibleInvoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleExportExcel = () => {
    const workbook = buildInvoiceBasWorkbook(visibleInvoices, timeframeRange.label);
    const fileName = `labourando-invoices-${timeframeRange.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${formatDateInput(new Date())}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const periodSummary = timeframe === 'this-fy'
    ? timeframeRange.label
    : timeframe === 'previous-fy'
      ? timeframeRange.label
      : customFrom && customTo
        ? `${customFrom} to ${customTo}`
        : 'Choose a date range';

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={4}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Invoices
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage your invoices
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<PersonIcon />}
              onClick={() => navigate('/invoice/profile')}
            >
              Billing Profile
            </Button>
            <Button
              variant="outlined"
              startIcon={<BusinessIcon />}
              onClick={() => navigate('/invoice/organisations')}
            >
              Organisations
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/invoice/new')}
            >
              New Invoice
            </Button>
          </Stack>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {/* Reporting Period */}
        <Paper
          sx={{
            p: 2,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0))',
          }}
        >
          <Stack spacing={1.5}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Reporting Period
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {periodSummary}
                </Typography>
              </Box>
              <Chip size="small" variant="outlined" icon={<CalendarIcon />} label={periodSummary} />
            </Box>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={timeframe}
              onChange={(_, nextValue) => {
                if (!nextValue) return;
                setTimeframe(nextValue);
                setPage(0);
              }}
              sx={{
                flexWrap: 'wrap',
                '& .MuiToggleButton-root': {
                  borderRadius: 999,
                  px: 2,
                  textTransform: 'none',
                  borderColor: 'divider',
                },
              }}
            >
              <ToggleButton value="this-fy">
                <HistoryIcon fontSize="small" sx={{ mr: 1 }} />
                This FY
              </ToggleButton>
              <ToggleButton value="previous-fy">
                <HistoryIcon fontSize="small" sx={{ mr: 1, transform: 'scaleX(-1)' }} />
                Previous FY
              </ToggleButton>
              <ToggleButton value="absolute">
                <CalendarIcon fontSize="small" sx={{ mr: 1 }} />
                Absolute dates
              </ToggleButton>
            </ToggleButtonGroup>

            {timeframe === 'absolute' && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderStyle: 'dashed',
                  borderColor: 'divider',
                  backgroundColor: 'rgba(255,255,255,0.02)',
                }}
              >
                <Stack spacing={1.25}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Custom date range
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Use this when you need an exact BAS or reporting window.
                      </Typography>
                    </Box>
                    <Chip size="small" color="primary" variant="outlined" label={periodSummary} />
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 1.5,
                    }}
                  >
                    <VisibleDateField
                      size="small"
                      label="From"
                      value={customFrom}
                      onChange={(e) => {
                        setCustomFrom(e.target.value);
                        setPage(0);
                      }}
                      helperText="Start of the reporting period"
                      sx={{ width: '100%' }}
                    />
                    <VisibleDateField
                      size="small"
                      label="To"
                      value={customTo}
                      onChange={(e) => {
                        setCustomTo(e.target.value);
                        setPage(0);
                      }}
                      helperText="End of the reporting period"
                      sx={{ width: '100%' }}
                    />
                  </Box>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Paper>

        {/* Insight Cards */}
        {dashboardInsights && (
          <Grid container spacing={2}>
            {insightCards.map((card) => {
              const Icon = card.icon;
              const captionText = card.count !== null ? `${card.count} invoice${card.count !== 1 ? 's' : ''}` : '\u00a0';
              return (
                <Grid key={card.label} size={{ xs: 6, md: 3 }}>
                  <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex' }}>
                    <Stack spacing={1} sx={{ height: '100%', width: '100%' }} justifyContent="space-between">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Icon sx={{ color: card.color, fontSize: 20 }} />
                        <Typography variant="body2" color="text.secondary">
                          {card.label}
                        </Typography>
                      </Box>
                      <Typography variant="h5" fontWeight={700}>
                        {card.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ minHeight: 20, visibility: card.count !== null ? 'visible' : 'hidden' }}>
                        {captionText}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}

        <Paper sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <ReceiptIcon color="primary" />
            <Typography variant="h6" fontWeight={600} sx={{ flexGrow: 1 }}>
              All Invoices
            </Typography>
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
              <MenuItem value="voided">Voided</MenuItem>
            </TextField>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              disabled={visibleInvoices.length === 0}
              sx={{ height: 40, whiteSpace: 'nowrap' }}
            >
              Export Excel
            </Button>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : visibleInvoices.length === 0 ? (
            <Box textAlign="center" py={6}>
              <MoneyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">
                {statusFilter ? 'No invoices match this filter' : 'No invoices yet. Create your first one!'}
              </Typography>
              {!statusFilter && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/invoice/new')}
                  sx={{ mt: 2 }}
                >
                  Create Invoice
                </Button>
              )}
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel active={sortField === 'invoiceNumber'} direction={sortField === 'invoiceNumber' ? sortDirection : 'asc'} onClick={() => handleSort('invoiceNumber')}>
                          Invoice #
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel active={sortField === 'issueDate'} direction={sortField === 'issueDate' ? sortDirection : 'asc'} onClick={() => handleSort('issueDate')}>
                          Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel active={sortField === 'dueDate'} direction={sortField === 'dueDate' ? sortDirection : 'asc'} onClick={() => handleSort('dueDate')}>
                          Due Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel active={sortField === 'organisation'} direction={sortField === 'organisation' ? sortDirection : 'asc'} onClick={() => handleSort('organisation')}>
                          Organisation
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel active={sortField === 'total'} direction={sortField === 'total' ? sortDirection : 'asc'} onClick={() => handleSort('total')}>
                          Total
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel active={sortField === 'status'} direction={sortField === 'status' ? sortDirection : 'asc'} onClick={() => handleSort('status')}>
                          Status
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedInvoices.map((inv) => (
                      <TableRow
                        key={inv.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          ...(inv.status === 'overdue' && {
                            borderLeft: '4px solid',
                            borderLeftColor: 'error.main',
                            backgroundColor: 'rgba(211, 47, 47, 0.08)',
                          }),
                          ...(inv.status === 'voided' && {
                            borderLeft: '4px solid',
                            borderLeftColor: 'text.disabled',
                            opacity: 0.7,
                            backgroundColor: 'action.hover',
                          }),
                        }}
                        onClick={() => navigate(`/invoice/${inv.id}`)}
                      >
                        <TableCell>
                          <Typography fontWeight={600}>{inv.invoiceNumber}</Typography>
                        </TableCell>
                        <TableCell>{new Date(inv.issueDate).toLocaleDateString('en-AU')}</TableCell>
                        <TableCell>{new Date(inv.dueDate).toLocaleDateString('en-AU')}</TableCell>
                        <TableCell>{inv.organisationSnapshot?.name || '—'}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600}>{formatCurrency(inv.total)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={inv.status}
                            color={statusColors[inv.status] || 'default'}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={visibleInvoices.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25]}
              />
            </>
          )}
        </Paper>
      </Stack>
    </Container>
  );
};

export default InvoiceDashboard;
